import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { ASPECT_RATIO_OPTIONS, MOBILE_ASPECT_INFO, mobileAspectOptions, } from '../_shared/aspect.js';
/** "The file decides" — the same head option the `image` section's Shape control opens with. */
const NATURAL = { value: 'natural', label: 'Natural (no crop)' };
/**
 * hero — the page-opening band. MIGRATED to the premium-calm theme token contract (the same one the new
 * sections use): COLOR comes from `--aqx-scheme-*` (driven by the universal `colorScheme` field) and
 * type / spacing / radius from the fixed `--aqx-theme-*` scale. The shared "Section" universal group
 * (colorScheme / paddingTop / paddingBottom / width) is appended so the inspector is consistent with
 * every other section.
 *
 * BACK-COMPAT: every original field id (heading, sub, ctaLabel, cta, bg, image, align, spacing) is kept
 * so existing documents still hydrate unchanged. The legacy `bg` color override and the legacy `spacing`
 * still win when set on an existing draft; a fresh section relies on the universal scheme + padding.
 */
export const heroSchema = {
    type: 'hero',
    name: 'Hero',
    version: 3,
    templates: ['index', 'page', 'thank-you'],
    fields: [
        // ── Content ──────────────────────────────────────────────────────────────
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'Content',
            default: { format: 'html', html: '<p>Bienvenue</p>' },
            info: 'Bold a word in the heading to apply the highlight.',
        },
        { id: 'sub', type: 'textarea', label: 'Subheading', group: 'Content', rows: 2, maxLength: 240 },
        // NEW (mockup "Button label") — the CTA's visible text, distinct from the link target.
        {
            id: 'ctaLabel',
            type: 'text',
            label: 'Button label',
            group: 'Content',
            placeholder: 'Discover',
            defaultKey: 'hero.shop_now',
            maxLength: 40,
        },
        { id: 'cta', type: 'link', label: 'Button link', group: 'Content' },
        {
            id: 'ctaStyle',
            type: 'select',
            label: 'Button style',
            group: 'Content',
            default: 'solid',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'outline', label: 'Outline' },
                { value: 'link', label: 'Text link' },
            ],
        },
        // Second button (Shrine image-banner ships two). Optional — left blank by default.
        {
            id: 'ctaLabel2',
            type: 'text',
            label: 'Second button label',
            group: 'Content',
            maxLength: 40,
        },
        { id: 'cta2', type: 'link', label: 'Second button link', group: 'Content' },
        {
            id: 'ctaStyle2',
            type: 'select',
            label: 'Second button style',
            group: 'Content',
            default: 'outline',
            options: [
                { value: 'solid', label: 'Solid' },
                { value: 'outline', label: 'Outline' },
                { value: 'link', label: 'Text link' },
            ],
        },
        { id: 'image', type: 'image', label: 'Background image', group: 'Content' },
        {
            id: 'imageMobile',
            type: 'image',
            label: 'Mobile background image',
            group: 'Content',
            info: 'Optional. If empty, the desktop image is used on mobile too.',
        },
        // An uploaded clip REPLACES the still behind the hero (founder ask 2026-09-16: the banner must be
        // able to hold a video, not only images). It plays exactly like the photo it replaces — silent,
        // looping, playsInline, NO player chrome — the same contract as sections/slideshow and
        // image-with-text. The still stays useful as the POSTER, so the band is never empty while the clip
        // is still deferred. Upload quality (Standard / Detail / Original) is chosen on the drop zone.
        {
            id: 'video',
            type: 'video',
            label: 'Background video',
            group: 'Content',
            info: 'Upload an MP4/WebM. When set, it replaces the background image and plays inline like one — silent, looping, no play bar.',
        },
        {
            id: 'videoMobile',
            type: 'video',
            label: 'Mobile background video',
            group: 'Content',
            info: 'Optional. If empty, the desktop video is used on mobile too.',
        },
        // Le disque lecture/pause sur la bande — éteint par défaut, parce qu'un héros n'en a jamais eu.
        // Il ne s'affiche dans l'inspecteur que lorsqu'un fichier est déposé : sur une image de fond, il
        // n'y aurait rien à mettre en pause. 19/09/2026.
        playButtonField({
            defaultOn: false,
            group: 'Content',
            visibleWhen: { field: 'video', empty: false },
        }),
        {
            id: 'align',
            type: 'select',
            label: 'Alignment',
            group: 'Content',
            default: 'center',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
        },
        // ── Heading highlight (shared engine) — style BOLD words in the rich-text heading. ────────────
        {
            id: 'headingHighlight1',
            type: 'select',
            label: 'Heading words highlight',
            group: 'Heading highlight',
            default: 'none',
            info: 'Bold a word in the heading (e.g. bold “summer”) to style it with this.',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid-color', label: 'Color' },
                { value: 'gradient-color', label: 'Gradient' },
                { value: 'bg-color', label: 'Background' },
                { value: 'underline-solid', label: 'Underline' },
                { value: 'underline-gradient', label: 'Gradient underline' },
                { value: 'line-1', label: 'Line 1' },
                { value: 'line-2', label: 'Line 2' },
                { value: 'line-3', label: 'Line 3' },
            ],
        },
        {
            id: 'headingHighlight1Color',
            type: 'color',
            label: 'Highlight text color',
            group: 'Heading highlight',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'headingHighlight1', equals: ['solid-color', 'bg-color'] },
        },
        {
            id: 'headingHighlight1Gradient',
            type: 'gradient',
            label: 'Highlight gradient',
            group: 'Heading highlight',
            default: 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)',
            visibleWhen: { field: 'headingHighlight1', equals: 'gradient-color' },
        },
        // ── Text (font sizes) — the heading + subheading, per device. ──────────────────────────────────
        // The seller asked (16/09) for a hero whose type she can size herself: the heading has always been
        // locked to the fluid `--aqx-theme-text-display` clamp and the subheading to `text-lg`, so a long
        // sentence over a photo could only be shortened, never shrunk. `h0` / `lg` ARE those two defaults,
        // which is why an existing draft renders byte-identical until she moves something.
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Text',
            default: 'h0',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: 'Text',
            min: 20,
            max: 120,
            step: 2,
            unit: 'px',
            default: 56,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: 'Text',
            min: 16,
            max: 80,
            step: 2,
            unit: 'px',
            default: 32,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'subSize',
            type: 'select',
            label: 'Subheading size',
            group: 'Text',
            default: 'lg',
            options: [
                { value: 'sm', label: 'Small' },
                { value: 'md', label: 'Medium' },
                { value: 'lg', label: 'Large' },
                { value: 'xl', label: 'XL' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'subheadingDesktopTextSize',
            type: 'range',
            label: 'Desktop subheading text size',
            group: 'Text',
            min: 12,
            max: 48,
            step: 1,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'subSize', equals: 'custom' },
        },
        {
            id: 'subheadingMobileTextSize',
            type: 'range',
            label: 'Mobile subheading text size',
            group: 'Text',
            min: 12,
            max: 40,
            step: 1,
            unit: 'px',
            default: 18,
            visibleWhen: { field: 'subSize', equals: 'custom' },
        },
        // LEGACY color override — kept for back-compat. A fresh hero leaves it empty and inherits the
        // universal `colorScheme` palette; an existing draft that set it still wins. Background resolves via
        // the canonical token contract; default is the REAL 'background' token (the shipped v1 default 'bg'
        // was bogus — §6.5; the migrate below repairs existing drafts).
        {
            id: 'bg',
            type: 'color',
            label: 'Background',
            group: 'Content',
            info: 'Optional override. Leave at the default to use the section color scheme below.',
            default: { ref: 'theme', token: 'bg' }, // 0019 §4a: 'background' renamed to the canonical 'bg'
        },
        // LEGACY spacing — kept for back-compat. When set it overrides the universal top/bottom padding so
        // existing drafts render exactly as before.
        {
            id: 'spacing',
            type: 'range',
            label: 'Spacing (legacy)',
            group: 'Content',
            info: 'Optional symmetric vertical padding. Leave at default to use the Section padding below.',
            default: 64,
            min: 0,
            max: 160,
            step: 8,
            unit: 'px',
        },
        // ── Banner (Shrine image-banner parity) — height + overlay. ─────────────────────────────────────
        {
            id: 'bannerHeight',
            type: 'select',
            label: 'Banner height',
            group: 'Banner',
            default: 'medium',
            // A chosen SHAPE sizes the band from the image's own proportions, so the fixed-height picker below
            // has nothing left to say — it would be a control whose value the render ignores. It comes back the
            // moment the shape returns to "Natural", with its stored value intact (`visibleWhen` never writes).
            visibleWhen: { field: 'bannerAspect', equals: 'natural' },
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'bannerHeightDesktop',
            type: 'range',
            label: 'Custom desktop height',
            group: 'Banner',
            min: 200,
            max: 1200,
            step: 10,
            unit: 'px',
            default: 520,
            visibleWhen: [
                { field: 'bannerHeight', equals: 'custom' },
                { field: 'bannerAspect', equals: 'natural' },
            ],
        },
        {
            id: 'bannerHeightMobile',
            type: 'range',
            label: 'Custom mobile height',
            group: 'Banner',
            min: 200,
            max: 700,
            step: 10,
            unit: 'px',
            default: 420,
            visibleWhen: [
                { field: 'bannerHeight', equals: 'custom' },
                { field: 'bannerAspect', equals: 'natural' },
            ],
        },
        // ── The IMAGE's own size — the `image` section's vocabulary, word for word. ─────────────────────
        //
        // Until now the hero could size the BAND (a height in pixels) and nothing else: the photo behind it
        // was painted `center / cover` with no control at all, so a 16:9 studio shot dropped into a 480px
        // band lost a third of itself and the seller had no knob to turn. These four are the same pair the
        // `image` section ships — a Shape and a width, once per device — so "how big is my picture" is
        // answered the same way in both places (founder, 18/09/2026).
        //
        // Shape TAKES OVER from `bannerHeight`: a ratio and a fixed pixel height are two answers to one
        // question, and the render obeys the ratio, so the height picker hides itself above rather than
        // sitting there lying. `natural` — the default, and what every hero already saved carries — keeps
        // the pixel height, so nothing on a live storefront moves.
        {
            id: 'bannerAspect',
            type: 'select',
            label: 'Image shape',
            group: 'Banner',
            default: 'natural',
            info: 'Natural keeps the banner height above. Any other shape sizes the band from the image’s proportions instead, so the photo is shown the way it was framed.',
            options: [NATURAL, ...ASPECT_RATIO_OPTIONS],
        },
        {
            id: 'bannerAspectMobile',
            type: 'select',
            label: 'Mobile image shape',
            group: 'Banner',
            default: 'inherit',
            info: MOBILE_ASPECT_INFO,
            options: mobileAspectOptions(NATURAL),
        },
        {
            id: 'bannerImageWidth',
            type: 'range',
            label: 'Image width',
            group: 'Banner',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 100,
            info: 'Under 100% the media is inset and centred, and the section colour shows down both sides.',
        },
        {
            id: 'bannerImageWidthMobile',
            type: 'range',
            label: 'Mobile image width',
            group: 'Banner',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            inheritFrom: 'bannerImageWidth',
        },
        {
            id: 'overlayOpacity',
            type: 'range',
            label: 'Image overlay opacity',
            group: 'Banner',
            info: 'A dark scrim over the background image so the text stays legible.',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 40,
        },
        // ── Layout (Shrine content-position grid + mobile). ─────────────────────────────────────────────
        {
            id: 'desktopContentPosition',
            type: 'select',
            label: 'Desktop content position',
            group: 'Layout',
            default: 'middle-center',
            options: [
                { value: 'top-left', label: 'Top left' },
                { value: 'top-center', label: 'Top center' },
                { value: 'top-right', label: 'Top right' },
                { value: 'middle-left', label: 'Middle left' },
                { value: 'middle-center', label: 'Middle center' },
                { value: 'middle-right', label: 'Middle right' },
                { value: 'bottom-left', label: 'Bottom left' },
                { value: 'bottom-center', label: 'Bottom center' },
                { value: 'bottom-right', label: 'Bottom right' },
            ],
        },
        {
            id: 'mobileContentAlignment',
            type: 'select',
            label: 'Mobile content alignment',
            group: 'Layout',
            default: 'center',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
        },
        {
            id: 'showTextBelowOnMobile',
            type: 'checkbox',
            label: 'Show text below image on mobile',
            group: 'Layout',
            default: false,
        },
        // ── Background pattern (shared engine — 13 Shrine patterns). ────────────────────────────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            info: 'A subtle tiling pattern behind the content (over the scheme colour / image).',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Slideshow — only applies when the section has 2+ "slide" blocks. ────────────────────────────
        {
            id: 'slideshowAutoplay',
            type: 'checkbox',
            label: 'Auto-rotate slides',
            group: 'Slideshow',
            default: false,
            info: 'Applies when the section has 2 or more slides (added as blocks).',
        },
        {
            id: 'slideshowAutoplaySpeed',
            type: 'range',
            label: 'Change slides every',
            group: 'Slideshow',
            min: 3,
            max: 12,
            step: 1,
            unit: 'sec',
            default: 6,
            visibleWhen: { field: 'slideshowAutoplay', equals: true },
        },
        {
            id: 'slideshowDots',
            type: 'select',
            label: 'Dots',
            group: 'Slideshow',
            default: 'under',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slides' },
            ],
        },
        {
            id: 'slideshowArrows',
            type: 'select',
            label: 'Arrows',
            group: 'Slideshow',
            default: 'sides',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'under', label: 'Under the slides' },
                { value: 'sides', label: 'On the sides' },
            ],
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal) — scheme / padding / width, consistent with every new section. ──────────
        ...universalFields,
    ],
    // Each "slide" block is one banner in the slideshow (used only when there are 2+; a single hero reads
    // its slide content from the section-level fields above, unchanged).
    blocks: [
        {
            type: 'slide',
            name: 'Slide',
            max: 8,
            fields: [
                {
                    id: 'heading',
                    type: 'richtext',
                    label: 'Heading',
                    default: { format: 'html', html: '<p>Titre de la diapositive</p>' },
                    info: 'Bold a word to apply the highlight.',
                },
                { id: 'sub', type: 'textarea', label: 'Subheading', rows: 2, maxLength: 240 },
                { id: 'ctaLabel', type: 'text', label: 'Button label', maxLength: 40 },
                { id: 'cta', type: 'link', label: 'Button link' },
                { id: 'image', type: 'image', label: 'Background image' },
                {
                    id: 'video',
                    type: 'video',
                    label: 'Video',
                    info: 'Upload an MP4/WebM. When set, it replaces the image on this slide and plays inline like one — silent, looping, no play bar.',
                },
                playButtonField({ defaultOn: false, visibleWhen: { field: 'video', empty: false } }),
                {
                    id: 'overlayOpacity',
                    type: 'range',
                    label: 'Image overlay opacity',
                    min: 0,
                    max: 100,
                    step: 5,
                    unit: '%',
                    default: 40,
                },
                {
                    id: 'desktopContentPosition',
                    type: 'select',
                    label: 'Content position',
                    default: 'middle-center',
                    options: [
                        { value: 'top-left', label: 'Top left' },
                        { value: 'top-center', label: 'Top center' },
                        { value: 'top-right', label: 'Top right' },
                        { value: 'middle-left', label: 'Middle left' },
                        { value: 'middle-center', label: 'Middle center' },
                        { value: 'middle-right', label: 'Middle right' },
                        { value: 'bottom-left', label: 'Bottom left' },
                        { value: 'bottom-center', label: 'Bottom center' },
                        { value: 'bottom-right', label: 'Bottom right' },
                    ],
                },
                {
                    id: 'headingHighlight1',
                    type: 'select',
                    label: 'Heading words highlight',
                    default: 'none',
                    info: 'Bold a word in the heading to style it with this.',
                    options: [
                        { value: 'none', label: 'None' },
                        { value: 'solid-color', label: 'Color' },
                        { value: 'gradient-color', label: 'Gradient' },
                        { value: 'underline-gradient', label: 'Gradient underline' },
                    ],
                },
                {
                    id: 'headingHighlight1Color',
                    type: 'color',
                    label: 'Highlight text color',
                    default: { ref: 'theme', token: 'accent' },
                    visibleWhen: { field: 'headingHighlight1', equals: 'solid-color' },
                },
            ],
        },
    ],
    /**
     * v1 → v2: derive ctaLabel from the old link.label, seed the new spacing default, and repair the
     * bogus `{ref:'theme',token:'bg'}` background so existing seeded drafts resolve to a real CSS var
     * instead of falling through to the field default (§6.5). The universal fields seed via applyDefaults.
     */
    migrate(fromVersion, settings) {
        if (fromVersion >= 2)
            return settings;
        const s = settings;
        // 0019 §4a reversed the token rename: 'background' -> the canonical 'bg'. A v1 doc holding the old
        // bogus 'bg' now passes through unchanged (it IS canonical); a doc holding the intermediate
        // 'background' is repaired to 'bg'. (hydrateDocument also remaps globally; this keeps the hero
        // migrate self-consistent for resolveSettings callers.)
        const bgIsLegacy = s.bg && s.bg.ref === 'theme' && s.bg.token === 'background';
        return {
            ...settings,
            ctaLabel: s.ctaLabel ?? s.cta?.label,
            spacing: s.spacing ?? 64,
            bg: bgIsLegacy ? { ref: 'theme', token: 'bg' } : s.bg,
        };
    },
};
export const heroPresets = [
    {
        name: 'Hero',
        settings: {
            heading: '@t:section.hero_heading',
            sub: 'Des pièces choisies à la main, conçues pour durer.',
            ctaLabel: '@t:hero.shop_now',
            cta: { type: 'collection', slug: 'all', label: '@t:hero.shop_now' },
            align: 'center',
            // AUCUNE image de démo (direction fondatrice 2026-09-16) : le héros s'insère en bandeau vide, et
            // `bannerHeight` (défaut 'medium') lui donne la hauteur qui fait apparaître le placeholder scénique
            // — « votre photo ici » — au lieu d'une photo maison que la vendeuse doit remplacer.
            colorScheme: 'light',
            paddingTop: 120,
            paddingBottom: 120,
            width: 'standard',
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const heroMeta = {
    label: 'Hero',
    group: 'Layout',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map